import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import HelpHint from "../components/HelpHint";
import type { User } from "@domaddress/shared";

type BackupRow = {
  key: string;
  size: number | undefined;
  lastModified: string | undefined;
};

type BackupsResponse =
  | { enabled: false; rolling: BackupRow[]; manual: BackupRow[]; pendingRestoreRestart: boolean }
  | { enabled: true; rolling: BackupRow[]; manual: BackupRow[]; pendingRestoreRestart: boolean };

/** Moet overeenkomen met `restoreSchema` op de server. */
const PREFERRED_RESTORE_PHRASE = "HERSTEL-DOMADDRESS";

function isRestorePhraseOk(s: string): boolean {
  const t = s.trim();
  return t === PREFERRED_RESTORE_PHRASE || t === "HERSTEL_DOMADDRESS";
}

function formatBytes(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/** Zelfde tabel-opbouw als Beheer (gebruikers / taken): `users-table` + horizontaal scrollen. */
function BackupsAdminTable({
  rows,
  emptyMessage,
  busy,
  onDownload,
  onDeleteManual,
  onOpenRestore,
}: {
  rows: BackupRow[];
  emptyMessage: string;
  busy: boolean;
  onDownload?: (key: string) => void;
  onDeleteManual?: (key: string) => void;
  onOpenRestore?: (key: string) => void;
}) {
  const hasDl = Boolean(onDownload);
  const hasRs = Boolean(onOpenRestore);
  const hasDel = Boolean(onDeleteManual);
  const nActions = (hasDl ? 1 : 0) + (hasRs ? 1 : 0) + (hasDel ? 1 : 0);
  const showActionColumn = nActions > 0;
  const colCount = showActionColumn ? 4 : 3;
  /* Ruim genoeg voor drie compacte knoppen op één regel (geen wrap). */
  const actionColW =
    nActions >= 3 ? "21rem" : nActions === 2 ? "13.5rem" : showActionColumn ? "7rem" : undefined;
  const tableMinW = showActionColumn ? Math.min(960, 460 + nActions * 160) : 440;

  return (
    <div className="table-wrap--scroll">
      <table
        className="users-table users-table--compact users-table--backups"
        style={{ marginBottom: 0, tableLayout: "fixed" as const, minWidth: tableMinW }}
      >
        <colgroup>
          <col />
          <col style={{ width: "7rem" }} />
          <col style={{ width: "13rem" }} />
          {showActionColumn ? <col style={{ width: actionColW }} /> : null}
        </colgroup>
        <thead>
          <tr>
            <th title="Object-sleutel in de cloud-bucket">Sleutel (key)</th>
            <th>Grootte</th>
            <th>Datum (UTC)</th>
            {showActionColumn ? <th className="users-table__th-actions">Acties</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="data-list-row-muted">
                <em>{emptyMessage}</em>
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.key}>
                <td
                  title={r.key}
                  style={{
                    wordBreak: "break-all",
                    whiteSpace: "normal",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                  }}
                >
                  {r.key}
                </td>
                <td>{formatBytes(r.size)}</td>
                <td style={{ whiteSpace: "nowrap" }} title={r.lastModified ?? ""}>
                  {r.lastModified ?? "—"}
                </td>
                {showActionColumn ? (
                  <td className="users-table__td-actions">
                    <div className="users-table__action-btns users-table__action-btns--backup">
                      {onDownload ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={busy}
                          title="Back-upbestand naar je computer downloaden"
                          onClick={() => onDownload(r.key)}
                        >
                          Download
                        </button>
                      ) : null}
                      {onOpenRestore ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={busy}
                          title="Database en uploads herstellen vanaf deze back-up"
                          onClick={() => onOpenRestore(r.key)}
                        >
                          Herstel
                        </button>
                      ) : null}
                      {onDeleteManual ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={busy}
                          title="Verwijderen in B2"
                          onClick={() => onDeleteManual(r.key)}
                        >
                          Verwijderen
                        </button>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function BeheerBackupsPanel({ user }: { user: User | null }) {
  const [data, setData] = useState<BackupsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [manualLabel, setManualLabel] = useState("");
  const [restoreModalKey, setRestoreModalKey] = useState<string | null>(null);
  const [restoreModalPhrase, setRestoreModalPhrase] = useState("");
  const [restoreModalLocalError, setRestoreModalLocalError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<BackupsResponse>("/api/beheer/backups");
      setData(res.data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data?.detail ??
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Laden mislukt";
      setError(String(msg));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const superAdmin = Boolean(user?.is_super_admin);
  const tableBusy = busy || downloading;

  function openRestoreModal(key: string) {
    setRestoreModalKey(key);
    setRestoreModalPhrase("");
    setRestoreModalLocalError(null);
  }

  function filenameForBackupDownloadKey(key: string): string {
    const base = key.split("/").pop() || "domaddress-backup.tar.gz";
    const s = base.replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 180);
    return s.length > 0 ? s : "domaddress-backup.tar.gz";
  }

  async function downloadBackup(key: string) {
    setDownloading(true);
    setError(null);
    try {
      const res = await api.post<Blob>("/api/beheer/backups/download", { key }, { responseType: "blob" });
      const blob = res.data;
      if (!(blob instanceof Blob) || blob.size === 0) {
        setError("Download mislukt (leeg antwoord).");
        return;
      }
      if (blob.type.includes("json")) {
        const text = await blob.text();
        let msg = "Download mislukt";
        try {
          const j = JSON.parse(text) as { error?: string; detail?: string };
          msg = [j.error, j.detail].filter(Boolean).join(" — ") || msg;
        } catch {
          /* ignore */
        }
        setError(msg);
        return;
      }
      const name = filenameForBackupDownloadKey(key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const err = e as { response?: { data?: Blob } };
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        let msg = "Download mislukt";
        try {
          const j = JSON.parse(text) as { error?: string; detail?: string };
          msg = [j.error, j.detail].filter(Boolean).join(" — ") || msg;
        } catch {
          /* ignore */
        }
        setError(msg);
      } else {
        setError("Download mislukt");
      }
    } finally {
      setDownloading(false);
    }
  }

  function closeRestoreModal() {
    if (busy) return;
    setRestoreModalKey(null);
    setRestoreModalPhrase("");
    setRestoreModalLocalError(null);
  }

  async function createManual() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/beheer/backups/manual", {
        label: manualLabel.trim() || undefined,
      });
      setManualLabel("");
      await load();
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data;
      setError([d?.error, d?.detail].filter(Boolean).join(" — ") || "Handmatige back-up mislukt");
    } finally {
      setBusy(false);
    }
  }

  async function deleteManual(key: string) {
    if (!window.confirm("Deze handmatige back-up permanent verwijderen in de cloud?")) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/beheer/backups/manual/delete", { key });
      await load();
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data;
      setError([d?.error, d?.detail].filter(Boolean).join(" — ") || "Verwijderen mislukt");
    } finally {
      setBusy(false);
    }
  }

  async function forceRolling() {
    if (!window.confirm("Nu een automatische (rolling) back-up maken? Oude rolling-back-ups kunnen daarna worden opgeruimd.")) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/beheer/backups/rolling/run");
      await load();
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data;
      setError([d?.error, d?.detail].filter(Boolean).join(" — ") || "Rolling back-up mislukt");
    } finally {
      setBusy(false);
    }
  }

  async function restartServerAfterRestore() {
    if (
      !window.confirm(
        "De server wordt nu beëindigd zodat deze opnieuw opstart (bijv. via PM2). Daarna wordt de voorbereide database-swap uitgevoerd. Doorgaan?"
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ ok?: boolean; message?: string }>("/api/beheer/backups/restart-after-restore");
      window.alert(res.data.message ?? "Herstart gestart.");
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string; detail?: string } }; message?: string };
      if (!err.response) {
        window.alert(
          "De verbinding is weggevallen — dat hoort zo als de server net herstart. Wacht enkele seconden en ververs de pagina."
        );
      } else {
        const d = err.response.data;
        setError([d?.error, d?.detail].filter(Boolean).join(" — ") || "Herstart mislukt");
      }
    } finally {
      setBusy(false);
      void load().catch(() => {});
    }
  }

  async function confirmRestoreFromModal() {
    const key = restoreModalKey;
    if (!key) return;
    if (!isRestorePhraseOk(restoreModalPhrase)) {
      setRestoreModalLocalError(
        `Typ exact: ${PREFERRED_RESTORE_PHRASE} (streepje tussen HERSTEL en DOMADDRESS). Het oudere HERSTEL_DOMADDRESS mag ook.`
      );
      return;
    }
    setRestoreModalLocalError(null);
    setBusy(true);
    setError(null);
    try {
      const phrase = restoreModalPhrase.trim();
      const res = await api.post<{ ok?: boolean; message?: string }>("/api/beheer/backups/restore", {
        key,
        confirmPhrase: phrase,
      });
      setRestoreModalKey(null);
      setRestoreModalPhrase("");
      await load();
      window.alert(res.data.message ?? "Herstel voorbereid. Gebruik de knop ‘Herstart server’ hieronder.");
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data;
      const msg = [d?.error, d?.detail].filter(Boolean).join(" — ") || "Herstel mislukt";
      setRestoreModalLocalError(msg);
    } finally {
      setBusy(false);
    }
  }

  const phraseOk = isRestorePhraseOk(restoreModalPhrase);

  if (loading && !data) {
    return <p className="text-muted">Back-ups laden…</p>;
  }

  const enabled = data?.enabled === true;

  return (
    <div className="detail-section" style={{ maxWidth: 900 }}>
      <div className="section-title-row" style={{ marginBottom: 8 }}>
        <h3 className="section-title" style={{ margin: 0 }}>
          Cloud back-ups (Backblaze B2)
        </h3>
        <HelpHint label="Uitleg: back-ups">
          <p>
            <strong>Automatisch (rolling):</strong> ongeveer eens per 24 uur maakt de server een archief (database + map{" "}
            <code>uploads/</code>, bijv. profielfoto&apos;s) en zet dat gecomprimeerd in de cloud. Een deploy of
            herstart maakt geen extra rolling back-up als er recent al een is. Er blijven maximaal zeven rolling-koppen;
            oudere worden automatisch verwijderd.
          </p>
          <p>
            <strong>Handmatig:</strong> extra kopieën die je zelf start. Alleen een <strong>superbeheerder</strong> mag
            die weer uit de cloud verwijderen.
          </p>
          <p>
            <strong>Download:</strong> iedereen met toegang tot dit scherm kan een back-up als bestand ophalen (
            <code>.tar.gz</code>: database + uploads). Oudere bestanden in de cloud kunnen nog het oude formaat (
            <code>.sqlite.gz</code>, alleen database) zijn; bewaar downloads veilig.
          </p>
          <p>
            <strong>Herstel:</strong> alleen superbeheerder — knop <strong>Herstel</strong> op een rij opent een
            venster. Daarna verschijnt <strong>Herstart server</strong>: daarmee start het proces opnieuw op en wordt de
            database en uploads omgezet (op productie draait de app onder PM2 met automatisch herstarten).
          </p>
        </HelpHint>
      </div>

      {error ? (
        <p style={{ color: "var(--accent, #c62828)", marginBottom: "1rem" }} role="alert">
          {error}
        </p>
      ) : null}

      {superAdmin && data?.pendingRestoreRestart ? (
        <div
          role="status"
          style={{
            marginBottom: "1.25rem",
            padding: "1rem 1.1rem",
            borderRadius: "var(--radius-md, 10px)",
            border: "1px solid var(--gold-500, #c9a227)",
            background: "rgba(201, 162, 39, 0.12)",
            maxWidth: 720,
          }}
        >
          <p style={{ margin: "0 0 0.75rem", fontSize: 15, lineHeight: 1.5, color: "var(--text-800)" }}>
            <strong>Herstel staat klaar.</strong> De back-up staat op de server; bij de volgende start worden het
            databasebestand en (indien in de back-up) de map uploads ingeruild. Klik hieronder om het proces nu te
            beëindigen zodat PM2 de server opnieuw opstart — daarna is de swap uitgevoerd.
          </p>
          <button type="button" className="btn-primary" disabled={tableBusy} onClick={() => void restartServerAfterRestore()}>
            {busy ? "Bezig…" : "Herstart server nu"}
          </button>
        </div>
      ) : null}

      {!enabled ? (
        <p style={{ marginBottom: "1rem" }}>
          Back-ups zijn niet geconfigureerd op de server (omgevingsvariabelen B2). Vraag de beheerder om{" "}
          <code>B2_BACKUP_ENABLED</code> en de bijbehorende sleutels in te stellen.
        </p>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: "1rem", alignItems: "center" }}>
        <button type="button" className="btn-secondary" disabled={tableBusy} onClick={() => void load()}>
          Vernieuwen
        </button>
        {enabled ? (
          <button type="button" className="btn-primary" disabled={tableBusy} onClick={() => void createManual()}>
            Handmatige back-up nu
          </button>
        ) : null}
        {enabled && superAdmin ? (
          <button type="button" className="btn-secondary" disabled={tableBusy} onClick={() => void forceRolling()}>
            Rolling back-up nu (super)
          </button>
        ) : null}
      </div>

      {enabled ? (
        <div className="form-group" style={{ maxWidth: 420, marginBottom: "1rem" }}>
          <label>Optioneel label voor handmatige back-up (max. 200 tekens)</label>
          <input
            type="text"
            className="filter-select"
            value={manualLabel}
            onChange={(e) => setManualLabel(e.target.value)}
            maxLength={200}
            disabled={tableBusy}
            style={{ width: "100%", maxWidth: "100%" }}
            placeholder="bijv. voor_grote_import"
          />
        </div>
      ) : null}

      {enabled && data ? (
        <>
          <h4 className="section-title" style={{ fontSize: "1rem", marginTop: "1.5rem", marginBottom: "0.5rem" }}>
            Automatisch (rolling)
          </h4>
          <BackupsAdminTable
            rows={data.rolling}
            emptyMessage="Nog geen rolling back-ups."
            busy={tableBusy}
            onDownload={(key) => void downloadBackup(key)}
            onOpenRestore={superAdmin ? openRestoreModal : undefined}
          />

          <h4 className="section-title" style={{ fontSize: "1rem", marginTop: "1.25rem", marginBottom: "0.5rem" }}>
            Handmatig
          </h4>
          <BackupsAdminTable
            rows={data.manual}
            emptyMessage="Nog geen handmatige back-ups."
            busy={tableBusy}
            onDownload={(key) => void downloadBackup(key)}
            onDeleteManual={superAdmin ? (key) => void deleteManual(key) : undefined}
            onOpenRestore={superAdmin ? openRestoreModal : undefined}
          />
        </>
      ) : null}

      {restoreModalKey != null ? (
        <div className="modal-overlay" onClick={() => closeRestoreModal()}>
          <div className="modal" style={{ maxWidth: 500, width: "100%" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ margin: 0 }}>
                Database herstellen
              </h2>
              <button type="button" className="modal-close" onClick={() => closeRestoreModal()} aria-label="Sluiten">
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "var(--text-700)" }}>
                <strong>Let op:</strong> hiermee bereid je het terugzetten van een volledige databasekopie voor. Na een
                geslaagde actie moet je de <strong>server herstarten</strong> (pm2/systemd of handmatig). Daarna is de
                huidige database vervangen — maak indien nodig eerst een nieuwe back-up.
              </p>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Back-up (key)</label>
                <div
                  style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    wordBreak: "break-all",
                    padding: "10px 12px",
                    background: "var(--surface-100, #f3f0f7)",
                    borderRadius: "var(--radius-md, 10px)",
                    border: "1px solid var(--surface-300, #e4dfe8)",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  {restoreModalKey}
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="backup-restore-phrase">Typ ter bevestiging exact</label>
                <input
                  id="backup-restore-phrase"
                  type="text"
                  className="filter-select"
                  value={restoreModalPhrase}
                  onChange={(e) => {
                    setRestoreModalPhrase(e.target.value);
                    setRestoreModalLocalError(null);
                  }}
                  disabled={tableBusy}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={PREFERRED_RESTORE_PHRASE}
                  style={{ width: "100%", maxWidth: "100%", fontFamily: "monospace" }}
                />
                <small style={{ fontSize: 12, color: "var(--text-500)", marginTop: 6, display: "block" }}>
                  Verwacht: <strong style={{ fontFamily: "monospace" }}>{PREFERRED_RESTORE_PHRASE}</strong> (met
                  streepje). Oud alternatief: <code>HERSTEL_DOMADDRESS</code>
                </small>
              </div>
              {restoreModalLocalError ? (
                <p className="form-error" style={{ margin: 0 }}>
                  {restoreModalLocalError}
                </p>
              ) : null}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => closeRestoreModal()} disabled={busy}>
                Annuleren
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={busy || !phraseOk}
                onClick={() => void confirmRestoreFromModal()}
              >
                {busy ? "Bezig…" : "Herstel voorbereiden"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
